import React from 'react';
import { Box, IconButton, Snackbar, Alert, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import { useGoogleTranslateLang } from '../../hooks/useGoogleTranslateLang';
import useSpeechRecognition, { getSpeechLang } from '../../hooks/useSpeechRecognition';

const ERROR_MESSAGES = {
  'not-allowed': 'Microphone access was blocked. Please allow microphone permission in your browser settings.',
  'service-not-allowed': 'Microphone access was blocked. Please allow microphone permission in your browser settings.',
  'no-speech': "Didn't catch that. Please tap the mic and speak again.",
  'audio-capture': 'No microphone found. Please check your device.',
  // A `network` error here almost always means the browser lacks a speech
  // backend (e.g. the in-app/Electron browser, Brave, or vanilla Chromium),
  // not an actual connectivity problem.
  network: 'Voice search isn\'t available in this browser. Please open the site in Google Chrome or Safari (or check your internet), then try again.',
  unsupported: "Voice search isn't supported on this browser. Please type your search instead.",
  'insecure-context': 'Voice search needs a secure connection. Open the site over https:// (or localhost) to use the microphone.',
  'start-failed': 'Could not start voice search. Please try again.',
};

const resolveErrorMessage = (error) =>
  ERROR_MESSAGES[error] || 'Voice search failed. Please try again.';

/**
 * Microphone button that fills a search field via the native Web Speech API.
 * Purely additive: it never blocks manual typing and stays out of the way when
 * the browser has no speech support (tapping just shows a friendly message).
 *
 * Props:
 *  - onInterimResult(text): live transcript while the user is speaking.
 *  - onFinalResult(text): final transcript once recognition completes.
 *  - size / iconFontSize / iconColor: styling knobs for each search-bar variant.
 */
const VoiceSearchButton = ({
  onInterimResult,
  onFinalResult,
  size = 'small',
  iconFontSize = 20,
  iconColor = '#323232',
  title = 'Search by voice',
  sx = {},
}) => {
  const gtLang = useGoogleTranslateLang();
  const speechLang = getSpeechLang(gtLang);

  const { listening, error, start, stop, clearError } = useSpeechRecognition({
    lang: speechLang,
    onInterimResult,
    onFinalResult,
  });

  const handleClick = (e) => {
    // Prevent the click from bubbling to parent handlers (e.g. mobile bar that
    // opens the service dialog) so the mic can start listening in place.
    e.stopPropagation();
    e.preventDefault();
    if (listening) stop();
    else start();
  };

  return (
    <>
      <Tooltip title={listening ? 'Listening… tap to stop' : title}>
        <IconButton
          onClick={handleClick}
          onMouseDown={(e) => e.stopPropagation()}
          size={size}
          aria-label={listening ? 'Stop voice search' : 'Start voice search'}
          className="notranslate"
          translate="no"
          sx={{
            color: listening ? '#d32f2f' : iconColor,
            transition: 'color 0.2s ease',
            position: 'relative',
            ...(listening && {
              animation: 'voiceSearchPulse 1.4s infinite',
              '@keyframes voiceSearchPulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.45)' },
                '70%': { boxShadow: '0 0 0 8px rgba(211,47,47,0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
              },
            }),
            ...sx,
          }}
        >
          <MicIcon sx={{ fontSize: iconFontSize }} />
        </IconButton>
      </Tooltip>

      {/* Floating "Listening…" indicator with animated wave bars. */}
      <Snackbar
        open={listening}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: (t) => t.zIndex.tooltip + 1 }}
      >
        <Box
          className="notranslate"
          translate="no"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            bgcolor: '#221b47',
            color: '#fff',
            px: 2,
            py: 1.25,
            borderRadius: 999,
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 18 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 3,
                  borderRadius: 2,
                  bgcolor: '#ff7a1a',
                  animation: 'voiceWave 1s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`,
                  '@keyframes voiceWave': {
                    '0%, 100%': { height: 5 },
                    '50%': { height: 16 },
                  },
                }}
              />
            ))}
          </Box>
          <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
            Listening…
          </Box>
        </Box>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clearError} severity="warning" variant="filled" sx={{ width: '100%' }}>
          {resolveErrorMessage(error)}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VoiceSearchButton;
